import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import useModalFocus from '../ui/useModalFocus';

const Chevron = ({ dir }) => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20" aria-hidden="true" focusable="false">
        {dir === 'left'
            ? <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            : <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />}
    </svg>
);
Chevron.propTypes = { dir: PropTypes.oneOf(['left', 'right']).isRequired };

/**
 * The photo viewer (P-UNIT-021) on the full unit page: ←/→ move and stop at
 * the ends, prev / next hidden at the ends, "i / n", × closes, a click on the
 * backdrop closes and a click on the photo does not, alt "MAKE MODEL — view
 * i". New on the page (N41): a modal dialog with a focus trap; focus returns
 * to what opened it. Escape closes only the viewer (the page stays, D6).
 */
export default function Lightbox({ images, index, onIndexChange, onClose, unit }) {
    const panelRef = useRef(null);
    const layerRef = useRef(null);
    const closeRef = useRef(null);
    const focusKeys = useModalFocus(panelRef, index !== null, { onEscape: onClose, initialFocusRef: closeRef, layerRef });
    if (index === null || !images.length) return null;
    const last = images.length - 1;

    const onKeyDown = (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); onIndexChange(Math.min(index + 1, last)); return; }
        if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); onIndexChange(Math.max(index - 1, 0)); return; }
        focusKeys(e);
    };

    return createPortal(
        <div className="ui-v2 u2-lb-layer" ref={layerRef} data-pane-overlay="true">
            {/* The backdrop is the dialog itself: a click outside the photo and the
                buttons closes it. */}
            <div
                ref={panelRef}
                className="u2-lb"
                role="dialog"
                aria-modal="true"
                aria-label="Photo viewer"
                tabIndex={-1}
                onKeyDown={onKeyDown}
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <img
                    src={images[index]}
                    alt={`${unit.MAKE} ${unit.MODEL} — view ${index + 1}`}
                    className="u2-lb-img"
                />
                <button ref={closeRef} type="button" className="u2-lb-btn u2-lb-x" onClick={onClose} aria-label="Close photo viewer">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20" aria-hidden="true" focusable="false">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
                {index > 0 && (
                    <button type="button" className="u2-lb-btn u2-lb-prev" onClick={() => onIndexChange(index - 1)} aria-label="Previous photo">
                        <Chevron dir="left" />
                    </button>
                )}
                {index < last && (
                    <button type="button" className="u2-lb-btn u2-lb-next" onClick={() => onIndexChange(index + 1)} aria-label="Next photo">
                        <Chevron dir="right" />
                    </button>
                )}
                <span className="u2-lb-cnt" aria-live="polite">{index + 1} / {images.length}</span>
            </div>
        </div>,
        document.body,
    );
}

Lightbox.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    index: PropTypes.number,
    onIndexChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    unit: PropTypes.object.isRequired,
};
